import { SeoLocale, SeoMetadata } from "../../http/protocol";
import { isFilled, isWebUrl } from "../seo/seoFallback";
import { SeoField, SeoMetadataByLocaleType } from "../seo/seoTypes";

type SeoDraftListenerType = () => void;

export type SeoDraftLoadStatusType = "loading" | "loaded" | "failed";

/** The part of a draft a component subscribes to, so it re-renders when the draft changes. */
export interface SeoDraftSubscriptionType {
  subscribe(listener: SeoDraftListenerType): () => void;
  getVersion(): number;
}

/** Indexes SEO entries by locale; entries without a locale are skipped. */
export function seoByLocale(entries?: SeoMetadata[]): SeoMetadataByLocaleType {
  const result: SeoMetadataByLocaleType = {};
  (entries ?? []).forEach((entry) => {
    if (entry.locale !== undefined) {
      result[entry.locale] = entry;
    }
  });
  return result;
}

/** A stored row in a locale no longer served would make the backend reject the whole request, so only served locales are sent. */
export function entriesInServedLocales(values: SeoMetadataByLocaleType, locales?: SeoLocale[]): SeoMetadata[] {
  const served = new Set((locales ?? []).map((locale) => locale.code));
  return Object.values(values).filter((entry) => served.has(entry.locale));
}

/** The values with one field of one locale replaced. */
export function withSeoField(values: SeoMetadataByLocaleType, locale: string, field: SeoField, value: string): SeoMetadataByLocaleType {
  return { ...values, [locale]: { ...values[locale], locale, [field]: value } };
}

/**
 * Why any draft refused to send its values after its target was saved; a draft adds its own codes. The UI
 * turns the code into translated copy.
 */
export type SeoSaveFailureCodeType = "invalid-og-image-url";

/**
 * Raised when the saved target cannot be matched to the submitted one, so the SEO values would land
 * on the wrong place, or when a draft holds a value the backend would reject. The SEO request is
 * then not sent at all.
 */
export class SeoSaveError<TCode extends string = SeoSaveFailureCodeType> extends Error {
  constructor(readonly code: TCode) {
    super(code);
    this.name = "SeoSaveError";
  }
}

/** Matches by name, since a transpiled subclass of Error does not reliably pass instanceof. */
export function isSeoSaveError<TCode extends string = SeoSaveFailureCodeType>(error: unknown): error is SeoSaveError<TCode> {
  return error instanceof Error && error.name === "SeoSaveError";
}

/** True when any entry has a filled OG image URL that is not an http(s) address; the backend rejects those. */
export function hasInvalidOgImageUrl(entries: SeoMetadata[]): boolean {
  return entries.some((entry) => isFilled(entry.ogImageUrl) && !isWebUrl(entry.ogImageUrl));
}

/**
 * Load, save and change tracking shared by the casino SEO drafts. A draft lives outside React so the
 * class-based views can own it and hand it to the function-based editors.
 */
export abstract class SeoDraftStore<TEditor> implements SeoDraftSubscriptionType {
  private listeners = new Set<SeoDraftListenerType>();
  private version = 0;
  private requestedId?: number;
  private loadedId?: number;
  private loadStatus?: SeoDraftLoadStatusType;
  private loadFailure?: unknown;
  private saving = false;

  protected abstract fetch(id: number): Promise<TEditor>;

  protected abstract applyLoaded(editor: TEditor): void;

  subscribe(listener: SeoDraftListenerType): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Changes on every update, so a subscriber can tell a stale snapshot from a fresh one. */
  getVersion(): number {
    return this.version;
  }

  getLoadedId(): number | undefined {
    return this.loadedId;
  }

  getLoadStatus(): SeoDraftLoadStatusType | undefined {
    return this.loadStatus;
  }

  getLoadFailure(): unknown {
    return this.loadFailure;
  }

  /** True while the SEO request is out. Its response replaces every value, so edits made meanwhile would be lost. */
  isSaving(): boolean {
    return this.saving;
  }

  /**
   * Starts loading a target's SEO unless it is already loading, loaded or failed for that target, so
   * several editors sharing one draft fetch it once.
   */
  ensureLoaded(id: number): void {
    if (this.requestedId === id || this.loadedId === id) {
      return;
    }
    this.loadStatus = "loading";
    this.loadFailure = undefined;
    this.notify();
    this.loadFor(id).catch((error: unknown) => {
      if (this.requestedId === id) {
        this.loadStatus = "failed";
        this.loadFailure = error;
        this.notify();
      }
    });
  }

  /** Fetches and loads a target's SEO; a response for a target no longer requested is dropped. */
  private async loadFor(id: number): Promise<void> {
    this.requestedId = id;
    const editor = await this.fetch(id);
    if (this.requestedId === id) {
      this.load(id, editor);
    }
  }

  /** Takes a fetched editor response as the target's loaded state. */
  load(id: number, editor: TEditor): void {
    this.loadedId = id;
    this.loadStatus = "loaded";
    this.applyLoaded(editor);
    this.notify();
  }

  /** Runs a save with the draft marked as saving, so the editors lock their fields until it settles. */
  protected async whileSaving<T>(save: () => Promise<T>): Promise<T> {
    this.saving = true;
    this.notify();
    try {
      return await save();
    } finally {
      this.saving = false;
      this.notify();
    }
  }

  protected notify(): void {
    this.version += 1;
    this.listeners.forEach((listener) => listener());
  }
}
